import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';

export type CertificateStatus = 'active' | 'revoked' | 'expired';

export type CertificateSummary = {
  id: string;
  label: string;
  kind: string;
  terminalProfileId: string | null;
  status: CertificateStatus;
  serialNumber: string | null;
  notBefore: string | null;
  notAfter: string | null;
  payloadSha256: string;
  revokedAt: string | null;
  revokedReason: string | null;
  createdAt: string;
  updatedAt: string;
};

export type CertificateListResponse = {
  items: CertificateSummary[];
  total: number;
  page: number;
  pageSize: number;
};

export type CertificateListQuery = {
  page?: number;
  pageSize?: number;
  status?: CertificateStatus[];
  terminalProfileId?: string;
  kind?: string;
};

const LIST_KEY = ['certificates'] as const;
const ONE_KEY = (id: string) => ['certificates', id] as const;

export function useCertificates(query: CertificateListQuery = {}) {
  return useQuery({
    queryKey: [...LIST_KEY, query] as const,
    queryFn: async (): Promise<CertificateListResponse> => {
      const params: Record<string, string | number> = {};
      if (query.page !== undefined) params.page = query.page;
      if (query.pageSize !== undefined) params.pageSize = query.pageSize;
      if (query.status && query.status.length > 0) {
        params['filter[status]'] = query.status.join(',');
      }
      if (query.terminalProfileId !== undefined && query.terminalProfileId !== '') {
        params['filter[terminalProfileId]'] = query.terminalProfileId;
      }
      if (query.kind !== undefined && query.kind !== '') {
        params['filter[kind]'] = query.kind;
      }
      const res = await apiClient.get<CertificateListResponse>('/certificates', { params });
      return res.data;
    },
  });
}

export function useCertificate(id: string | undefined) {
  return useQuery({
    queryKey: id === undefined ? ['certificates', '__noop'] : ONE_KEY(id),
    enabled: id !== undefined,
    queryFn: async (): Promise<CertificateSummary> => {
      const res = await apiClient.get<CertificateSummary>(`/certificates/${id}`);
      return res.data;
    },
  });
}

export type CertificateUploadBody = {
  label: string;
  kind: string;
  terminalProfileId?: string | null;
  serialNumber?: string | null;
  notBefore?: string | null;
  notAfter?: string | null;
  payloadBase64: string;
};

export function useUploadCertificate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CertificateUploadBody): Promise<CertificateSummary> => {
      const res = await apiClient.post<CertificateSummary>('/certificates', body);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: LIST_KEY });
    },
  });
}

export type CertificatePatchBody = {
  label?: string;
  terminalProfileId?: string | null;
  serialNumber?: string | null;
  notBefore?: string | null;
  notAfter?: string | null;
};

export function usePatchCertificate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { id: string; body: CertificatePatchBody }) => {
      const res = await apiClient.patch<CertificateSummary>(`/certificates/${vars.id}`, vars.body);
      return res.data;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: LIST_KEY });
      qc.invalidateQueries({ queryKey: ONE_KEY(vars.id) });
    },
  });
}

export function useRevokeCertificate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { id: string; reason: string }) => {
      const res = await apiClient.post<CertificateSummary>(`/certificates/${vars.id}/revoke`, {
        reason: vars.reason,
      });
      return res.data;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: LIST_KEY });
      qc.invalidateQueries({ queryKey: ONE_KEY(vars.id) });
    },
  });
}

export function useRevealCertificate() {
  return useMutation({
    mutationFn: async (id: string): Promise<{ payloadBase64: string }> => {
      const res = await apiClient.post<{ payloadBase64: string }>(`/certificates/${id}/reveal`, {});
      return res.data;
    },
  });
}

export const STATUS_LABEL: Record<CertificateStatus, string> = {
  active: 'Active',
  revoked: 'Revoked',
  expired: 'Expired',
};

/**
 * Catalogue keys for the map above. The map stays as the baked English, so a
 * caller resolves a label as `t(STATUS_LABEL_KEY[s], STATUS_LABEL[s])`. Keys
 * are literals here because the parity guard reads source for literal keys.
 */
export const STATUS_LABEL_KEY: Record<CertificateStatus, string> = {
  active: 'certificates.vocab.statusActive',
  revoked: 'certificates.vocab.statusRevoked',
  expired: 'certificates.vocab.statusExpired',
};
